import usePrefetch from '../hooks/usePrefetch';
import Breadcrumbs from './Breadcrumbs';
import { CartContext } from './context/CartContext';
import {
    Link,
    useLocation,
    useParams,
    useSearchParams
} from 'react-router-dom';
import { LoadingContext } from './context/LoadingContext';
import ProductGallery from './ProductGallery';
import React, {
    lazy,
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import '../styles/Product.css';
import SEO from './SEO';
import AppContext from './context/AppContext';
import hasStoreName from '../helpers/hasStoreName';
import ProductAddToCartFormSkeleton from './ProductAddToCartFormSkeleton';
import useBuildProductStructuredData from '../hooks/useBuildProductStructuredData';
import PropTypes from 'prop-types';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';
import { ToastContext } from './context/ToastContext';
import Tooltip from 'react-bootstrap/Tooltip';
import protectCopyRight from '../helpers/protectCopyRight';
import LoadingFallback from './LoadingFallback';
import LazyLoadWhenInView from './LazyLoadWhenInView';
import { MdLocalShipping } from 'react-icons/md';
import { BiDollar } from 'react-icons/bi';
import { FaThumbsUp } from 'react-icons/fa6';
import { RiForward30Fill } from 'react-icons/ri';
import LiaShippingFastSolid from '../icons/LiaShippingFastSolid';
import getSymbolFromCurrency from 'currency-symbol-map';
import { Button, Placeholder, Tab, Nav } from 'react-bootstrap';
import { BsBox } from 'react-icons/bs';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { IoAlertCircleOutline } from 'react-icons/io5';
import MiniProductReviewsBlock from './MiniProductReviewsBlock';
import ProductPDFViewer from './ProductPDFViewer';


const NotFound = lazy(() => import('./NotFound'));
const ProductReviews = lazy(() => import('./ProductReviews'));
const ProductAddToCartForm = lazy(() => import('./ProductAddToCartForm'));
const ReportProductIssueModal = lazy(() => import('./ReportProductIssueModal'));
const ProductOftenBoughtWithProducts = lazy(() => import('./ProductOftenBoughtWithProducts'));
const QuestionsAndAnswers = lazy(() => import('./QuestionsAndAnswers'));
const AnswerBaseProductWidget = lazy(() => import('./AnswerbaseProductWidget'));
const ProductDescription = lazy(() => import('./ProductDescription'));
const ProductAddedToCartOverlay =lazy(() => import('./ProductAddedToCartOverlay'));

/** CONFIGURATION */
const BASE_URL = process.env.APP_URL.replace(/\/$/, '');
export const MAX_PRODUCT_VIEWED = 25;
const { COMPATIBILITY_CHECK_ARTICLE_ID, SHIPPING_DETAIL_ARTICLE_ID } = constants.PRODUCT;
const CLASS_NAMES = {
    FEATURE: 'd-flex flex-column w-25',
    ICON_LABEL: 'product-page--icon-label',
    ICON_WRAPPER: 'product-page--icon-wrapper',
};

/** HELPERS */

export const fetchProductData = async (handle) => {
    const response = await fetch(`${BASE_URL}/api/products/handle/${handle}`);
    const data = await response.json();
    return data?.product ?? null;
};

export const getMatchingVariant = (variants, selectedOptions) => {
    return variants.filter(
        (variant) => {
            if (!variant.Options.length) {
                return false;
            }

            return variant.Options.every((option) => {
                const selectedOptionValue = selectedOptions.get(option.id);
                if (!selectedOptionValue) {
                    return false;
                }
                const { optionValueSelected } = option.VariantSelectedOptions;
                return (selectedOptionValue === optionValueSelected);
            });
        }
    ).at(0) ?? null;
};

/**
 * Component for showing the product page.
 * @component
 */
const Product = ({ isMobile }) => {
    /** Contexts */
    const { appContext } = useContext(AppContext);
    const { sendMessage } = useContext(ToastContext);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const productBasePath = productPath[0].pathBeforeParams;
    const appConfig = appContext.config.themeConfig;
    const { cartState } = useContext(CartContext);
    const { startLoading, finishLoading } = useContext(LoadingContext);
    const [stateLoaded, setStateLoaded] = useState(false);
    const structuredData = useBuildProductStructuredData();

    /** States */
    const [ breadcrumbList, setBreadcrumbList ] = useState([]);
    const [ errorMessage, setErrorMessage ] = useState(null);
    const [ isEligibleForFreeShipping, setIsEligibleForFreeShipping ] = useState(false);
    const [ maxQuantity, setMaxQuantity ] = useState(0);
    const [ price, setPrice ] = useState(0);
    const [ selectedOptions, setSelectedOptions ] = useState(new Map());
    const [ selectedVariant, setSelectedVariant ] = useState(null);
    const [ totalCartQuantity, setTotalCartQuantity ] = useState(0);
    const [freeShippingAmount, setFreeShippingAmount] = useState(0);
    const [loadingFreeShippingAmount, setLoadingFreeShippingAmount] = useState(true);
    const [showCartModal, setShowCartModal] = useState(false);

    /** URL-related Data */
    const location = useLocation();
    const { collectionHandle, handle } = useParams();
    const [ searchParams ] = useSearchParams();

    /** Model States */


    // Pre-fetch product data.
    const { isLoaded: productLoaded, data: product, error } = usePrefetch({
        key: 'product',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            await startLoading();
            const data = await fetchProductData(handle);
            await finishLoading();
            return data;
        },
    });
    const fetchProductFreeShippingEligibility = async () => {
        try {
            const response = await fetch(`${BASE_URL}/api/products/handle/${handle}/isEligibleForFreeShipping`);
            const data = await response.json();
            if (data.isEligibleForFreeShipping === true) {
                setIsEligibleForFreeShipping(true);
            } else {
                setIsEligibleForFreeShipping(false);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const constructBreadcrumbs = useCallback(async () => {
        try {
            const breadcrumbs = [[product.title, location.pathname]];

            if (!product?.Collections.length) {
                setBreadcrumbList(breadcrumbs);
                setStateLoaded(true);
                return;
            }

            const url = new URL(`/api/collections/${product.Collections[0].id}/upper-hierarchy`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            });
            const { hierarchy, error } = await response.json();

            if (error) {
                sendMessage(error, 'danger');
            }

            if (hierarchy) {
                hierarchy.default.reverse().forEach(collection => {
                    breadcrumbs.unshift([
                        collection.title,
                        `${collectionPath[0].pathBeforeParams}/${collection.handle}`,
                    ]);
                });

                setBreadcrumbList(breadcrumbs);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setStateLoaded(true);
        }
    }, [product, location.pathname]);

    const fetchFreeShippingAmount = useCallback(async () => {
        try {
            const url = new URL('/api/shippingPolicies/freeShippingAmount', process.env.APP_URL);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            });
            const { freeShippingAmount, error } = await response.json();

            if (error) {
                sendMessage(error, 'danger');
            }

            if (freeShippingAmount) {
                setFreeShippingAmount(freeShippingAmount);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoadingFreeShippingAmount(false);
        }
    }, []);

    useEffect(() => {
        fetchFreeShippingAmount();
    }, [fetchFreeShippingAmount]);

    // Load product data
    useEffect(() => {
        if (!product) {
            return;
        }
        if (error) {
            setErrorMessage(error.message);
            return;
        }
        const { Variants: variants } = product;
        if (variants.length === 1) {
            const defaultVariant = variants.at(0);
            setPrice(defaultVariant.price);
            setSelectedVariant(defaultVariant);
        } else {
            setPrice(product.price);
            setSelectedVariant(null);
        }
        setSelectedOptions(new Map());
        constructBreadcrumbs();
    }, [
        collectionHandle,
        error,
        location.pathname,
        product,
    ]);
    // Update variant from searchParams
    useEffect(() => {
        const variantId = searchParams.get('variant');
        if (product
            && variantId) {
            const { Variants: variants } = product;
            const selectedVariant = variants?.filter((v) => v.id === parseInt(variantId)).at(0) ?? null;
            setSelectedVariant(selectedVariant);
            const newSelectedOptions = new Map();
            if (selectedVariant) {
                for (const option of selectedVariant.Options) {
                    newSelectedOptions.set(option.id, option.VariantSelectedOptions.optionValueSelected);
                }
            }
            setSelectedOptions(newSelectedOptions);
            setPrice(selectedVariant.price);
        }
    }, [product, searchParams]);

    // Update total & max. qty on cart state changes
    useEffect(() => {
        let cartQty = 0;
        let maxQty = 0;
        const variant = selectedVariant ?? (product?.Variants.length === 1
            ? product?.Variants.at(0)
            : product?.Variants.find(
                (variant) => variant.title === 'Default Title'
                    || variant.variantPosition === 1
            )
        );
        if (variant) {
            maxQty = variant.availableQuantity;
            const inventoryLevels = variant.availableInventoryLevels.filter((l) => l.variantId === variant.id);
            inventoryLevels.forEach((inventoryLevel) => {
                if (inventoryLevel.bypassNegativeQuantity === true) {
                    maxQty = Infinity;
                }
                const cartItem = cartState.find((i) => i.inventoryLevelId === inventoryLevel.id);
                if (cartItem) {
                    if (maxQty !== Infinity) {
                        maxQty -= cartItem.quantity;
                    }
                    cartQty += cartItem.quantity;
                }
            });
        }
        setMaxQuantity(maxQty);
        setTotalCartQuantity(cartQty);
    }, [selectedVariant, cartState, product]);

    // Update recently viewed products in localStorage
    useEffect(() => {
        if (product) {
            // eslint-disable-next-line no-undef
            const recentlyViewed = localStorage.getItem('recentlyViewedProducts')?.split(',').filter(Boolean) ?? [];
            const newRecentlyViewed = [product.id,
                ...recentlyViewed.filter((p) => p !== product.id)]
                .slice(0, MAX_PRODUCT_VIEWED)
                .join(',');
            // eslint-disable-next-line no-undef
            localStorage.setItem('recentlyViewedProducts', newRecentlyViewed);
        }
    }, [product]);

    // Fetch the product's free shipping eligibility status
    useEffect(() => {
        fetchProductFreeShippingEligibility();
    }, []);

    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!productLoaded || !product) {
            return '';
        }
        let title = product.seoTitle ?? product.title;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [productLoaded, product]);

    const images = useMemo(() => (product?.Images || []).filter((image) => !image.imagePlacementId), [product, productLoaded]);

    /* Determine whether or not all options have been selected */
    const isAllOptionsSelected = useMemo(() => {
        if (!productLoaded || !product || !selectedOptions.size) {
            return false;
        }
        const { Options: options } = product;
        return options.every((o) => selectedOptions.get(o.id) !== undefined);
    }, [productLoaded, product, selectedOptions]);

    /* Set selected option */
    const selectOption = useCallback((optionId, value) => {
        if (!productLoaded || !product) {
            return;
        }
        const currentValue = selectedOptions.get(optionId);
        if (!currentValue && value === '') {
            return;
        }
        const newSelectedOptions = new Map(selectedOptions);
        if (value === '') {
            newSelectedOptions.delete(optionId);
        } else {
            newSelectedOptions.set(optionId, value);
        }
        setSelectedOptions(newSelectedOptions);
        const selectedVariant = getMatchingVariant(product.Variants, newSelectedOptions);
        setSelectedVariant(selectedVariant);
        if (selectedVariant !== null) {
            setPrice(selectedVariant.price);
        }
    }, [productLoaded, product, selectedOptions, getMatchingVariant]);

    const renderTooltip = (props) => (
        <Tooltip
            {...props}
        >
            <div
                className={'product-page--free-shipping-tooltip'}
            >
                <p>FREE SHIPPING:</p>
                <p>Free shipping on orders over ${freeShippingAmount} (mainland US only). For orders outside the mainland, or involving oversized, overweight, or oddly shaped items, please contact us for a quote or our representative will contact you after you place the order.</p>
                <Link
                    to={`/Articles.asp?ID=${SHIPPING_DETAIL_ARTICLE_ID}`}>More Shipping Details</Link>
            </div>
        </Tooltip>
    );

    /* Render content */
    const content = () => {
        if (errorMessage) {
            return <p>{errorMessage}</p>;
        }
        if (productLoaded === false) {
            return <p>Fetching product...</p>;
        }
        if (productLoaded === true && !product) {
            return (
                <Suspense
                    fallback={<LoadingFallback />}
                >
                    <NotFound />
                </Suspense>
            );
        }
        return <>
            <SEO
                seoTitle={title}
                seoDescription={product.seoDescription}
                indexed={product.isIndexed}
                seoType={'product'}
                canonicalUrl={new URL(`${productBasePath}/${product.handle}`, process.env.APP_URL).toString()}
                breadsArr={[
                    {
                        handle: '/',
                        title: 'Home',
                    },
                    {
                        title: 'Products',
                        handle: productBasePath,
                    },
                    {
                        title: product.seoTitle ?? product.title,
                        handle: `${productBasePath}/${product.handle}`,
                    },
                ]}
                image={product.imageUrl}
                richSnippet={structuredData(product)}
            />
            <ProductAddedToCartOverlay
                isMobile={isMobile}
                show={showCartModal}
                setShow={setShowCartModal}/>
            <Breadcrumbs
                breadcrumbList={breadcrumbList}
                loading={!stateLoaded} />
            <img
                onContextMenu={protectCopyRight}
                src={appConfig.productTopBannerUrl}
                alt={'Product top banner'}
                className={'product--top-banner'}
            />
            <div
                className={'product-page--main-content'}>
                <div
                    className={'flex-fill'}>
                    {isMobile && <h1>{product.title}</h1>}
                    {!isMobile && <h1
                        className={'mb-0 pb-2'}
                        dangerouslySetInnerHTML={{ __html: product.title }}></h1>}
                    {product?.vendor && <p>
                        by <Link
                            className={'product-page--vendor-name mb-3'}
                            to={`/searchresults.asp?v=${encodeURIComponent(product.vendor)}`} >
                            {product.vendor}
                        </Link>
                    </p>}
                    <div
                        className={`product-page--info${stateLoaded ? '' : ' loading'}`}>
                        <ProductGallery
                            product={product}
                            images={images}
                            models={product.Model3Ds}
                            videos={product.Metafields.filter((metafield) => metafield.metafieldKey === constants.YOUTUBE_METAFIELD_KEY)}
                        />
                    </div>
                    <div
                        className={'product--shipping-images'}
                    >
                        {appConfig.shippingDetailImages?.map((imageUrl, i) => (
                            <img
                                onContextMenu={protectCopyRight}
                                key={i}
                                src={imageUrl}
                                alt={`Shipping image ${i}`}
                                className={'product--shipping-image'}
                                width={388}
                                height={75}
                            />
                        ))}
                    </div>
                </div>

                <div
                    className={'product-page--details'}>
                    {!!stateLoaded && (<div
                        className={'product-page--price-container mb-0'}>
                        {price === 0.0 ? (
                            <span>Please call us for price on this product.</span>
                        ): (
                            <div
                                className={'d-flex align-items-center'}>
                                <span
                                    className={'product-page--price'}>${(Math.round(price * 100) / 100).toFixed(2)}</span>
                                <span
                                    style={{fontSize: '.8em'}}
                                    className={'ms-2 text-nowrap'}> { isEligibleForFreeShipping === true ? '+ Free Shipping' : '+ Calculated at checkout'}</span>

                            </div>
                        )}
                        {
                            (
                                (
                                    (selectedVariant?.availableForSale === true || selectedVariant?.availableForSale === 1)
                                  && !isNaN(selectedVariant?.compareAtPrice)
                                  && selectedVariant?.compareAtPrice > selectedVariant?.price
                                )
                                ||
                                (
                                    (product?.availableForSale === true || product?.availableForSale === 1)
                                  && !isNaN(product?.compareAtPrice)
                                  && product?.compareAtPrice > product?.price
                                )
                            )
                            && (<span>
                                <span>{' '}</span>
                                <span
                                    className={'text-decoration-line-through'}>{getSymbolFromCurrency(appConfig.storeCurrency)}{(selectedVariant? selectedVariant.compareAtPrice : product.compareAtPrice).toFixed(2)} {appConfig.storeCurrency}</span>
                                <div
                                    className={'mt-3 text-success'}>
                                    <b>Save</b> <span> {getSymbolFromCurrency(appConfig.storeCurrency)}{(selectedVariant? selectedVariant.compareAtPrice - selectedVariant.price : product.compareAtPrice - product.price).toFixed(2)} {appConfig.storeCurrency}</span>
                                </div>
                            </span>)
                        }
                    </div>)}
                    <MiniProductReviewsBlock
                        avgRating={product.avgRating}
                        totalReviews={product.totalReviews}
                        loading={!stateLoaded}
                    />
                    <div
                        className={'mb-3 fw-bolder'}
                    >
                        Check compatibility.&nbsp;<Link
                            to={`/Articles.asp?ID=${COMPATIBILITY_CHECK_ARTICLE_ID}`}
                            style={{textDecoration: 'underline'}} >Ask our experts</Link>.
                    </div>
                    {isEligibleForFreeShipping === true && (<div>
                        <LiaShippingFastSolid /> This product MAY qualify for Free Shipping.
                    </div>)}
                    {loadingFreeShippingAmount ? (
                        <Placeholder
                            animation={'glow'}
                        >
                            <Placeholder
                                xs={8}
                                style={{ backgroundColor: 'rgb(188 188 188)' }}
                            />
                        </Placeholder>
                    ) : (
                        freeShippingAmount === -1 ? (
                            <div>No Free Shipping Options Available</div>
                        ) : (
                            <div>
                                <BsBox /> Orders over ${freeShippingAmount} Ships for FREE!
                                <OverlayTrigger
                                    placement={'bottom'}
                                    delay={{
                                        show: 250,
                                        hide: 1000
                                    }}
                                    overlay={renderTooltip}
                                >
                                    <Button
                                        variant={'link'}
                                        style={{
                                            paddingLeft: 0,
                                            color: 'black',
                                        }}
                                    >
                                        <IoAlertCircleOutline
                                            className={'product--alert-icon'}
                                        />
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        )
                    )}
                    <div
                        className={'product-page--icons-container my-3'}>
                        <div
                            className={CLASS_NAMES.FEATURE}>
                            <BiDollar
                                className={CLASS_NAMES.ICON_WRAPPER}
                                style={{background: '#db9090'}}
                                size={40}/>
                            <div
                                className={CLASS_NAMES.ICON_LABEL}>
                                Lowest price guarantee</div>
                        </div>
                        <div
                            className={CLASS_NAMES.FEATURE}>
                            <RiForward30Fill
                                className={CLASS_NAMES.ICON_WRAPPER}
                                size={40}/>
                            <div
                                className={CLASS_NAMES.ICON_LABEL}>
                                30 Days Return</div>
                        </div>
                        <div
                            className={CLASS_NAMES.FEATURE}>
                            <FaThumbsUp
                                className={CLASS_NAMES.ICON_WRAPPER}
                                size={40}/>
                            <div
                                className={CLASS_NAMES.ICON_LABEL}>
                                Guaranteed Deliver</div>
                        </div>
                        <div
                            className={CLASS_NAMES.FEATURE}>
                            <MdLocalShipping
                                className={CLASS_NAMES.ICON_WRAPPER}
                                size={40}/>
                            <div
                                className={CLASS_NAMES.ICON_LABEL}>
                                {isEligibleForFreeShipping ? 'Free' : 'Flat-Rate Shipping'} </div>
                        </div>
                    </div>
                    {maxQuantity > 0 && (
                        <div
                            className={'fw-semibold'}>
                            <span
                                style={{
                                    color: 'green',
                                }}>In Stock.</span> Same Day Shipping
                        </div>
                    )}

                    {!stateLoaded && (<p
                        className={'product-page--price-container'}>
                    </p>)}
                    {!!stateLoaded && (<Suspense
                        fallback= {<LoadingFallback />}
                    >
                        <ProductAddToCartForm
                            isProductAvailableForSale={product.availableForSale}
                            isAllOptionsSelected={isAllOptionsSelected}
                            maxQuantity={maxQuantity}
                            options={product.Options}
                            price={price}
                            selectedOptions={selectedOptions}
                            selectOption={selectOption}
                            selectedVariant={selectedVariant}
                            variants={product.Variants}
                            totalCartQuantity={totalCartQuantity}
                            setShowCartModal={setShowCartModal}
                        />
                    </Suspense>
                    )}
                    {!stateLoaded && (<ProductAddToCartFormSkeleton />)}
                    <Suspense
                        fallback={<LoadingFallback />}
                    >
                        <ReportProductIssueModal
                            productId={product.id} />
                    </Suspense>
                </div>
            </div>
            <div
                className={'product--bottom-banner'}>
                <a
                    href={'/pallet-jack-parts/'}>
                    <img
                        onContextMenu={protectCopyRight}
                        src={appConfig.productBottomBannerUrl}
                        alt={'Product bottom banner'}
                    />
                </a>
            </div>

            <Tab.Container
                className={'mb-3'}
                defaultActiveKey={'description'}
                fill>
                <div
                    className={'w-100'}>
                    <Nav
                        className={'product-page--tab-nav'}
                        variant={'tabs'}>
                        <Nav.Item
                            className={'d-flex'}>
                            <Nav.Link
                                eventKey={'description'}>
                                Description
                            </Nav.Link>
                            {product.PDFs && product.PDFs.length > 0 && <Nav.Link
                                eventKey={'pdfs'}>
                                Manuals
                            </Nav.Link>}
                        </Nav.Item>
                    </Nav>
                </div>
                <div
                    className={'w-100'}>
                    <Tab.Content
                        className={'product-page--tab-content'}>
                        <Tab.Pane
                            eventKey={'description'}>
                            {product.descriptionHtml && String(product.descriptionHtml).trim().length > 0
                                ?  <LazyLoadWhenInView>
                                    <ProductDescription
                                        descriptionHtml={product.descriptionHtml}
                                    />
                                </LazyLoadWhenInView>
                                : <div>Fast Shipping. Order Today!</div>
                            }
                        </Tab.Pane>
                        <Tab.Pane
                            eventKey={'pdfs'}
                        >
                            <div
                                className={'d-flex gap-2'}>
                                {product.PDFs && product.PDFs.length > 0 && product.PDFs.map((pdf, index) =>
                                    <ProductPDFViewer
                                        key={index}
                                        pdf={pdf}
                                    />
                                )}
                            </div>
                        </Tab.Pane>
                    </Tab.Content>
                </div>
            </Tab.Container>
            <Suspense
                fallback={<LoadingFallback />}
            >
                <div
                    id={'q&a'}
                >
                    <QuestionsAndAnswers
                        productId={product.id}
                    />
                </div>
            </Suspense>

            <Suspense
                fallback={<LoadingFallback />}
            >
                <ProductOftenBoughtWithProducts
                    isMobile={isMobile}
                    productHandle={product.handle}
                />
            </Suspense>
            <Suspense
                fallback={<LoadingFallback />}
            >
                <ProductReviews
                    product={product}
                />
            </Suspense>

            {/* @todo */}
            <Suspense
                fallback={<LoadingFallback />}
            >
                <AnswerBaseProductWidget
                    data={{
                        productId: product.productCode ?? product.id,
                        productSku: product.sku,
                        productTitle: product.title,
                        productPrice: product.price,
                        productUrl: new URL(`${productBasePath}/${product.handle}`, process.env.APP_URL).toString(),
                        productDescription: product.seoDescription,
                        productImageUrl: product.Images[0]?.url ?? undefined,
                    }}
                />
            </Suspense>
        </>;
    };

    return (
        <div
            className={'section'}
        >
            {content()}
        </div>
    );
};

Product.propTypes = {
    isMobile: PropTypes.bool,
};

export default Product;
